import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { TrophyIcon, MagnifyingGlassIcon, FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { supabase, fetchHoleScoresPaginated } from '../../lib/supabase';
import { useTournament } from '../TournamentContext';
import { computeLeaderboard, sortLeaderboard, formatRoundScore, scoreColorHex, type ScoringMethod } from '../../lib/scoring';

interface LeaderboardEntry {
  id: string;
  player_name: string;
  country: string;
  category: string;
  handicap: number;
  total_score: number;
  round_scores: number[];
  rounds_played: number;
  position: number;
  overall_status?: string;
  thru: number;
}

interface RoundInfo {
  id: string;
  round_number: number;
  name: string;
}

interface TournamentInfo {
  id: string;
  name: string;
  status: string;
}

export default function MobileLeaderboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paramTournamentId = searchParams.get('tournamentId');
  const { selectedTournament, tournaments } = useTournament();
  const [activeTournament, setActiveTournament] = useState<TournamentInfo | null>(null);
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [filtered, setFiltered] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [categories, setCategories] = useState<string[]>([]);
  const [showFilter, setShowFilter] = useState(false);
  const [selectedRound, setSelectedRound] = useState(0);
  const [rounds, setRounds] = useState<RoundInfo[]>([]);
  const [roundPairedPlayerIds, setRoundPairedPlayerIds] = useState<Set<string>>(new Set());
  const [tournamentLogo, setTournamentLogo] = useState<string | null>(null);
  const [scoringMethod, setScoringMethod] = useState<ScoringMethod>('stroke_play_net');

  useEffect(() => {
    supabase
      .from('logos')
      .select('url')
      .eq('type', 'primary')
      .eq('location', 'header')
      .eq('is_active', true)
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.url) setTournamentLogo(data.url);
      });
  }, []);

  useEffect(() => {
    if (paramTournamentId) {
      const match = tournaments.find(t => t.id === paramTournamentId);
      setActiveTournament(match || { id: paramTournamentId, name: '', status: '' });
    } else if (tournaments.length > 0) {
      const ongoing = [...tournaments].reverse().find(t => t.status?.toLowerCase() === 'ongoing');
      if (ongoing) {
        setActiveTournament(ongoing);
      } else {
        setActiveTournament(tournaments[tournaments.length - 1]);
      }
    } else if (selectedTournament) {
      setActiveTournament(selectedTournament);
    }
  }, [paramTournamentId, tournaments, selectedTournament]);

  const tournamentId = activeTournament?.id || null;

  useEffect(() => {
    if (tournamentId) {
      fetchLeaderboard();
    }
  }, [tournamentId]);

  useEffect(() => {
    if (selectedRound > 0 && tournamentId) {
      const roundInfo = rounds.find(r => r.round_number === selectedRound);
      if (roundInfo) {
        fetchRoundPairings(roundInfo.id);
      } else {
        setRoundPairedPlayerIds(new Set());
      }
    } else {
      setRoundPairedPlayerIds(new Set());
    }
  }, [selectedRound, rounds, tournamentId]);

  useEffect(() => {
    let result = entries;
    if (category !== 'All') {
      result = result.filter(e => e.category === category);
      // Recalculate positions within filtered category for overall view
      if (selectedRound === 0) {
        let pos = 1;
        result = result.map((e, i) => {
          if (e.overall_status !== 'PLAYED') return { ...e, position: 0 };
          if (i > 0 && e.total_score === result[i - 1].total_score && result[i - 1].overall_status === 'PLAYED') {
            const newEntry = { ...e, position: result[i - 1].position };
            pos = i + 2;
            return newEntry;
          }
          const newEntry = { ...e, position: pos };
          pos = i + 2;
          return newEntry;
        });
      }
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(e =>
        e.player_name.toLowerCase().includes(q) ||
        e.country.toLowerCase().includes(q)
      );
    }
    if (selectedRound > 0) {
      result = result
        .filter(e => {
          const hasScore = e.round_scores?.[selectedRound - 1] != null;
          const isPaired = roundPairedPlayerIds.has(e.id);
          return hasScore || isPaired || e.overall_status === 'PLAYED';
        })
        .sort((a, b) => {
          const aScore = a.round_scores?.[selectedRound - 1];
          const bScore = b.round_scores?.[selectedRound - 1];
          const aHasScore = aScore != null;
          const bHasScore = bScore != null;
          if (aHasScore && !bHasScore) return -1;
          if (!aHasScore && bHasScore) return 1;
          if (aHasScore && bHasScore) return scoringMethod === 'stableford_net' ? bScore - aScore : aScore - bScore;
          return a.player_name.localeCompare(b.player_name);
        });

      // Assign positions with tie handling
      let pos = 1;
      result = result.map((e, i) => {
        const score = e.round_scores?.[selectedRound - 1];
        const hasScore = score != null;
        if (!hasScore) return { ...e, position: 0 };
        const prevScore = i > 0 ? result[i - 1].round_scores?.[selectedRound - 1] : null;
        const prevHasScore = prevScore != null;
        if (i > 0 && prevHasScore && score === prevScore) {
          const newEntry = { ...e, position: result[i - 1].position };
          pos = i + 2;
          return newEntry;
        }
        const newEntry = { ...e, position: pos };
        pos = i + 2;
        return newEntry;
      });
    }
    setFiltered(result);
  }, [entries, category, search, selectedRound, roundPairedPlayerIds]);

  const fetchRoundPairings = async (roundId: string) => {
    const { data } = await supabase
      .from('player_pairings')
      .select('player_id')
      .eq('round_id', roundId);
    if (data) {
      setRoundPairedPlayerIds(new Set(data.map(p => p.player_id)));
    }
  };

  const fetchLeaderboard = async () => {
    if (!tournamentId) return;
    try {
      const [leaderboardRes, roundsRes, tournamentRes, disabledRes] = await Promise.all([
        supabase
          .from('leaderboard_view')
          .select('*')
          .eq('tournament_id', tournamentId)
          .order('total_score', { ascending: true }),
        supabase
          .from('tournament_rounds')
          .select('*')
          .eq('tournament_id', tournamentId)
          .order('sort_order')
          .order('round_number'),
        supabase
          .from('system_settings')
          .select('value')
          .eq('key', `scoring_method_${tournamentId}`)
          .maybeSingle(),
        supabase
          .from('system_settings')
          .select('value')
          .eq('key', `disabled_rounds_${tournamentId}`)
          .maybeSingle(),
      ]);
      if (leaderboardRes.error) throw leaderboardRes.error;

      const disabledArr: number[] = Array.isArray(disabledRes.data?.value) ? disabledRes.data.value : [];

      const method: ScoringMethod = (tournamentRes.data?.value as ScoringMethod) || 'stroke_play_net';
      setScoringMethod(method);

      const data = leaderboardRes.data;
      if (data) {
        const playerIds = data.map(e => e.id);
        let holeScoreMap: Record<string, { round: number; hole: number; strokes: number; score_status?: string }[]> = {};

        const { data: tournamentCourse } = await supabase
          .from('tournament_courses')
          .select('course_id')
          .eq('tournament_id', tournamentId)
          .maybeSingle();

        let holeParMap: Record<number, number> = {};
        let holeHcpMap: Record<number, number> = {};
        if (tournamentCourse?.course_id) {
          const { data: holesData } = await supabase
            .from('holes')
            .select('hole_number, par, handicap_index')
            .eq('course_id', tournamentCourse.course_id)
            .order('hole_number');
          if (holesData) {
            for (const h of holesData) {
              holeParMap[h.hole_number] = h.par;
              holeHcpMap[h.hole_number] = h.handicap_index ?? h.hole_number;
            }
          }
        }

        if (playerIds.length > 0) {
          const holeRows = await fetchHoleScoresPaginated(tournamentId, playerIds);
          for (const row of holeRows) {
            if (!holeScoreMap[row.player_id]) holeScoreMap[row.player_id] = [];
            holeScoreMap[row.player_id].push({ round: row.round, hole: row.hole, strokes: row.strokes, score_status: row.score_status });
          }
        }

        const disabledSet = new Set(disabledArr);
        let localEnabledRounds: Set<number> | undefined = undefined;
        if ((roundsRes.data ?? []).length > 0) {
          localEnabledRounds = new Set<number>(
            (roundsRes.data ?? []).filter(r => !disabledSet.has(r.round_number)).map(r => r.round_number)
          );
        }

        const withScores = data.map(entry => {
          const playerHoles = holeScoreMap[entry.id];
          if (!playerHoles || playerHoles.length === 0) return { ...entry, thru: 0 };

          const computed = computeLeaderboard(
            [{ id: entry.id, handicap: entry.handicap ?? 0 }],
            holeScoreMap,
            holeParMap,
            holeHcpMap,
            method,
            localEnabledRounds,
          )[0];

          return {
            ...entry,
            total_score: computed.total_score,
            round_scores: computed.round_scores,
            rounds_played: computed.rounds_played,
            overall_status: entry.overall_status || 'PLAYED',
            thru: computed.thru,
          };
        });

        const activePlayers = withScores.filter(e => e.overall_status !== 'WD');
        const sorted = sortLeaderboard(activePlayers, method);

        // Reassign positions after sorting, handling ties
        let currentPosition = 1;
        for (let i = 0; i < sorted.length; i++) {
          if (sorted[i].overall_status !== 'PLAYED') {
            sorted[i].position = 0;
            continue;
          }
          if (i > 0 && sorted[i].total_score === sorted[i - 1].total_score && sorted[i - 1].overall_status === 'PLAYED') {
            sorted[i].position = sorted[i - 1].position;
          } else {
            sorted[i].position = currentPosition;
          }
          currentPosition = i + 2;
        }

        setEntries(sorted);
        const cats = ['All', ...Array.from(new Set(activePlayers.map((e: LeaderboardEntry) => e.category).filter(Boolean)))];
        setCategories(cats);
        const fetchedRounds: RoundInfo[] = (roundsRes.data ?? [])
          .filter(r => !disabledSet.has(r.round_number))
          .map((r, i) => ({
            id: r.id,
            round_number: r.round_number ?? i + 1,
            name: r.name || `R${r.round_number ?? i + 1}`,
          }));
        setRounds(fetchedRounds);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-golf-green border-t-transparent" />
      </div>
    );
  }


  return (
    <div className="pb-8 min-h-[60vh]">
      {/* Hero header with gradient - matching player details style */}
      <div className="relative bg-gradient-to-br from-golf-green to-golf-green/90 px-4 pt-5 pb-6 rounded-b-3xl">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-white border-2 border-white/80 flex items-center justify-center flex-shrink-0 overflow-hidden shadow-sm">
            <img
              src={tournamentLogo || 'https://wjwcuakqeesxnbjznajh.supabase.co/storage/v1/object/public/images/ibomopen2026.png'}
              alt={activeTournament?.name || 'Ibom Open'}
              className="w-10 h-10 object-contain"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-white truncate">
              {activeTournament?.name || 'Leaderboard'}
            </h1>
            <p className="text-sm text-white/70 mt-0.5">Leaderboard</p>
            {activeTournament?.status && (
              <span className="inline-flex items-center gap-1.5 text-xs text-white/60 bg-white/10 px-2.5 py-0.5 rounded-full mt-1.5">
                {activeTournament.status === 'ongoing' && (
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                )}
                {activeTournament.status === 'ongoing' ? 'Live' : activeTournament.status === 'upcoming' ? 'Upcoming' : 'Completed'}
              </span>
            )}
          </div>
        </div>

      </div>

      {/* Search bar */}
      <div className="px-4 pt-1 pb-3">
        <div className="relative">
          <MagnifyingGlassIcon className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search players..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-10 py-2.5 bg-white border border-gray-200 rounded-full text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-golf-green/20 focus:border-golf-green/40 shadow-sm"
          />
          {search ? (
            <button onClick={() => setSearch('')} className="absolute right-3.5 top-1/2 -translate-y-1/2">
              <XMarkIcon className="w-4 h-4 text-gray-400" />
            </button>
          ) : (
            <button
              onClick={() => setShowFilter(v => !v)}
              className={`absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full ${showFilter ? 'text-golf-green' : 'text-gray-400'}`}
            >
              <FunnelIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Category filter chips */}
      {showFilter && (
        <div className="px-4 pb-3 flex gap-2 overflow-x-auto scrollbar-hide">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                category === cat
                  ? 'bg-golf-green text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* Round Selector - pill style */}
      {rounds.length > 0 && (
        <div className="px-4 pb-4">
          <div className="flex gap-1.5 p-1 bg-gray-100 rounded-xl overflow-x-auto scrollbar-hide">
            <button
              onClick={() => setSelectedRound(0)}
              className={`flex-shrink-0 px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                selectedRound === 0
                  ? 'bg-golf-green text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Overall
            </button>
            {rounds.map((round, idx) => {
              const roundColors = [
                { active: 'bg-blue-500 text-white shadow-sm', inactive: 'text-blue-600 bg-blue-50' },
                { active: 'bg-amber-500 text-white shadow-sm', inactive: 'text-amber-600 bg-amber-50' },
                { active: 'bg-emerald-500 text-white shadow-sm', inactive: 'text-emerald-600 bg-emerald-50' },
                { active: 'bg-rose-500 text-white shadow-sm', inactive: 'text-rose-600 bg-rose-50' },
                { active: 'bg-cyan-500 text-white shadow-sm', inactive: 'text-cyan-600 bg-cyan-50' },
              ];
              const color = roundColors[idx % roundColors.length];
              return (
                <button
                  key={round.round_number}
                  onClick={() => setSelectedRound(round.round_number)}
                  className={`flex-shrink-0 px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                    selectedRound === round.round_number
                      ? color.active
                      : color.inactive
                  }`}
                >
                  {round.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="text-center py-20 px-4">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <TrophyIcon className="w-8 h-8 text-gray-300" />
          </div>
          <p className="text-gray-500 text-sm font-medium">No players found</p>
          <p className="text-gray-400 text-xs mt-1">Try adjusting your search or filters</p>
        </div>
      ) : (
        <div className="px-4">
          {/* Player count */}
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
              {filtered.length} Player{filtered.length !== 1 ? 's' : ''}
            </span>
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
              {selectedRound > 0 ? rounds.find(r => r.round_number === selectedRound)?.name || `R${selectedRound}` : 'Total'}
            </span>
          </div>

          {/* Player list */}
          <div className="rounded-2xl overflow-hidden border border-gray-100 bg-white shadow-sm">
            {filtered.map((entry, i, arr) => {
              const roundScore = selectedRound > 0
                ? (entry.round_scores?.[selectedRound - 1] ?? null)
                : entry.total_score;
              const hasScore = selectedRound > 0
                ? (roundScore != null)
                : true;
              const isLast = i === arr.length - 1;

              return (
                <div
                  key={entry.id}
                  onClick={() => navigate(`/m/player/${entry.id}`)}
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer active:bg-gray-50 transition-colors ${!isLast ? 'border-b border-gray-50' : ''}`}
                >
                  {/* Position */}
                  <div className="w-7 flex-shrink-0">
                    <span className={`text-xs font-bold ${entry.position > 0 && entry.position <= 3 ? 'text-golf-green' : 'text-gray-400'}`}>
                      {entry.position > 0 ? entry.position : '-'}
                    </span>
                  </div>

                  {/* Player info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-gray-800 truncate leading-tight">{entry.player_name}</p>
                    <p className="text-[11px] text-gray-400 truncate mt-0.5">
                      {entry.country}
                      {entry.category ? ` \u00B7 ${entry.category}` : ''}
                      {entry.thru > 0 ? ` \u00B7 ${entry.thru >= 18 ? 'F' : `Thru ${entry.thru}`}` : ''}
                    </p>
                  </div>

                  {/* Score */}
                  <div className="flex-shrink-0">
                    <span
                      className="text-sm font-bold tabular-nums"
                      style={{ color: hasScore ? scoreColorHex(roundScore, scoringMethod) : '#d1d5db' }}
                    >
                      {hasScore ? formatRoundScore(roundScore, scoringMethod) : '--'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
